/**
 * Search Management - Moon Moon Agency
 * Handles search overlay functionality
 */

/*********************************************************************************************
 * 🔍 SEARCH MODULE
 */
const SearchManager = {
	// Local configuration
	config: {
		selectors: {
			searchContainer: '[data-search-container]',
			searchInput: '[data-search-input]',
			searchToggle: '[data-search-toggle]',
			predictiveSearch: '[data-predictive-search]',
			searchSort: '[data-search-sort]'
		},
		classes: {
			stopScrolling: 'mm-stop-scrolling',
			open: 'mm-open'
		},
		timing: {
			searchFocusDelay: 100,
			searchBlurDelay: 100
		}
	},

	// Focus trap state
	focusTrap: {
		isActive: false,
		triggerElement: null,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null
	},

	/**
	 * Setup focus trap for search overlay
	 */
	setupFocusTrap(searchContainer, triggerElement) {
		const focusableElements = searchContainer.querySelectorAll(
			'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);

		if (focusableElements.length === 0) return;

		this.focusTrap.triggerElement = triggerElement;
		this.focusTrap.firstFocusable = focusableElements[0];
		this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1];

		// Handle Tab and Escape keys
		this.focusTrap.handleKeyDown = (e) => {
			// Escape key closes search
			if (e.key === 'Escape' || e.key === 'Esc') {
				e.preventDefault();
				this.hide();
				return;
			}

			// Tab key traps focus
			if (e.key !== 'Tab') return;

			if (e.shiftKey) {
				// Shift + Tab: going backwards
				if (document.activeElement === this.focusTrap.firstFocusable) {
					e.preventDefault();
					this.focusTrap.lastFocusable?.focus();
				}
			} else {
				// Tab: going forwards
				if (document.activeElement === this.focusTrap.lastFocusable) {
					e.preventDefault();
					this.focusTrap.firstFocusable?.focus();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		window.log('info', 'SearchManager', 'Focus trap activated');
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.triggerElement = null;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;
			window.log('info', 'SearchManager', 'Focus trap removed');
		}
	},

	/**
	 * Show search overlay
	 */
	show(triggerElement) {
		const elements = {
			search: window.getElement(this.config.selectors.searchContainer),
			html: document.documentElement,
			searchInput: window.getElement(this.config.selectors.searchInput)
		};

		if (Object.values(elements).some((el) => !el)) {
			window.log('warn', 'SearchManager', 'Required elements not found');
			return;
		}

		const { search, html, searchInput } = elements;

		html.classList.add(this.config.classes.stopScrolling);
		search.classList.add(this.config.classes.open);

		// Use NavigationManager for background filter
		if (window.NavigationManager?.showBackgroundFilter) {
			window.NavigationManager.showBackgroundFilter();
		}

		setTimeout(() => {
			searchInput.focus();
			// Setup focus trap after search is shown
			this.setupFocusTrap(search, triggerElement);
		}, this.config.timing.searchFocusDelay);

		// Touch handling for mobile
		const predictiveSearch = window.getElement(this.config.selectors.predictiveSearch);
		if (predictiveSearch) {
			predictiveSearch.addEventListener('touchstart', () => {
				setTimeout(() => searchInput.blur(), this.config.timing.searchBlurDelay);
			});
		}

		window.emit('global:search-opened');
	},

	/**
	 * Hide search overlay
	 */
	hide(keepFilterActive = false, keepStopScrolling = false) {
		const elements = {
			search: window.getElement(this.config.selectors.searchContainer),
			html: document.documentElement,
			searchInput: window.getElement(this.config.selectors.searchInput)
		};

		if (Object.values(elements).some((el) => !el)) {
			window.log('warn', 'SearchManager', 'Required elements not found for hiding');
			return;
		}

		const { search, html, searchInput } = elements;

		if (!keepStopScrolling) {
			html.classList.remove(this.config.classes.stopScrolling);
		}
		search.classList.remove(this.config.classes.open);

		// Remove focus trap when search closes, puis focus rendu au déclencheur
		// (lu avant : removeFocusTrap le remet à null)
		const trigger = this.focusTrap.triggerElement;
		const focusWasInside = search.contains(document.activeElement);
		this.removeFocusTrap();
		if (focusWasInside) trigger?.focus();

		// Use NavigationManager for background filter
		if (!keepFilterActive && window.NavigationManager?.hideBackgroundFilter) {
			window.NavigationManager.hideBackgroundFilter();
		}

		searchInput.value = '';
		searchInput.dispatchEvent(new Event('input'));

		window.emit('global:search-closed', { keepFilterActive, keepStopScrolling });
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.SearchManager = SearchManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
window.mmShowSearch = (triggerElement) => SearchManager.show(triggerElement);
window.mmHideSearch = (keepFilter, keepScrolling) => SearchManager.hide(keepFilter, keepScrolling);

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	// Enable search transition after load (prevents slide on page render — mobile only)
	const searchContainer = document.querySelector('[data-search-container]');
	if (searchContainer) {
		requestAnimationFrame(() => searchContainer.classList.add('mm-search-ready'));
	}

	// Search close buttons (background overlay + mobile header)
	document.querySelectorAll('[data-search-close]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const searchInput = document.getElementById('Search');
			if (searchInput) searchInput.value = '';
			SearchManager.hide();
		});
	});

	// Search clear button (inside search input)
	const searchClearBtn = document.querySelector('[data-search-clear]');
	if (searchClearBtn) {
		searchClearBtn.addEventListener('click', () => {
			const searchInput = document.getElementById('Search');
			if (searchInput) searchInput.value = '';
			SearchManager.hide();
		});
	}

	// Search results sort
	const sortSelect = document.querySelector(SearchManager.config.selectors.searchSort);
	if (sortSelect) {
		sortSelect.addEventListener('change', function() {
			const url = new URL(window.location.href);
			url.searchParams.set('sort_by', this.value);
			window.location.href = url.toString();
		});
	}
});